import { useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ArrowDown, ArrowLeft, Copy, ExternalLink, KeyRound, ListTodo, Pause, Play, FlaskConical, X } from 'lucide-react';
import { Abas, PainelAba } from '../../componentes/abas';
import { Botao, Cartao, Segmentado, Selo } from '../../componentes/base';
import { Estado, Vazio } from '../../componentes/estados';
import { Entrada, Formulario, Rotulo, Selecao, type DefCampo } from '../../componentes/formulario';
import { Confirmar, Drawer, Modal } from '../../componentes/sobreposicao';
import { Tabela, type Coluna } from '../../componentes/tabela';
import { TextoCortado } from '../../componentes/texto-cortado';
import { useToast } from '../../componentes/toast';
import { del, get, getTodos, mensagemDeErro, patch, post } from '../../nucleo/api';
import { data, dataHora, nomePessoa, numero, relativo, textoLivre } from '../../nucleo/formato';
import { useSessao } from '../../nucleo/sessao';
import { chavePainel } from './Painel';
import { AREAS, BASE, COR_ESTADO, duracao, type Execucao, type Ficha, type Pendencia, type Resultado } from './tipos';
import { LinkVoltar } from '../../nucleo/voltar';

type AbaFicha = 'fluxo' | 'execucoes' | 'pendencias' | 'resultados' | 'configuracao';
const ABAS: AbaFicha[] = ['fluxo', 'execucoes', 'pendencias', 'resultados', 'configuracao'];
const ID_ABAS = 'ficha-automacao';
const TIPOS_RESULTADO: Resultado['tipo'][] = ['relatorio', 'planilha', 'arquivo', 'pasta', 'link'];
const COR_EXECUCAO: Record<Execucao['estado'], 'perigo' | 'azul' | 'verde' | 'laranja'> = { falhou: 'perigo', rodando: 'azul', concluida: 'verde', sem_sinal: 'laranja' };

/**
 * Ficha da automação: Fluxo, Execuções, Pendências, Resultados (entrega 2) e Configuração (proposta, tela 2). O aviso do
 * sino e o card do Tarefas abrem a aba pelo `?aba=` (e a pendência pelo `?pendencia=`).
 */
export function FichaAutomacao() {
  const { t } = useTranslation('automacoes');
  const { id = '' } = useParams();
  const [params] = useSearchParams();
  const [aba, setAba] = useState<AbaFicha>(() => (ABAS as string[]).includes(params.get('aba') ?? '') ? (params.get('aba') as AbaFicha) : 'fluxo');
  const q = useQuery({ queryKey: ['automacoes', 'ficha', id], queryFn: () => get<Ficha>(`/automacoes/automacoes/${id}`), refetchInterval: 60_000 });
  return (
    <Estado consulta={q}>
      {(f) => (
        <div className="flex min-w-0 flex-col gap-5" data-ficha-automacao={f.codigo}>
          <Cabecalho f={f} />
          <Abas<AbaFicha> idBase={ID_ABAS} rotulo={f.nome} ativa={aba} aoMudar={setAba} itens={ABAS.map((k) => ({ chave: k, rotulo: t(`ficha.abas.${k}`) }))} />
          <PainelAba idBase={ID_ABAS} chave={aba}>
            {aba === 'fluxo' && <Fluxo f={f} />}
            {aba === 'execucoes' && <Execucoes f={f} />}
            {aba === 'pendencias' && <Pendencias f={f} />}
            {aba === 'resultados' && <Resultados f={f} />}
            {aba === 'configuracao' && <Configuracao f={f} />}
          </PainelAba>
        </div>
      )}
    </Estado>
  );
}

function Cabecalho({ f }: { f: Ficha }) {
  const { t } = useTranslation('automacoes');
  const s = useSessao();
  const qc = useQueryClient();
  const toast = useToast();
  const comando = useMutation({
    mutationFn: (tipo: string) => post(`/automacoes/automacoes/${f.id}/comandos`, { tipo }),
    onSuccess: async () => {
      toast.sucesso(t('ficha.pedido'));
      await qc.invalidateQueries({ queryKey: ['automacoes'] });
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  const pendentes = f.comandos_pendentes.map((c) => t(`comandos.${c.tipo}`)).join(', ');
  return (
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div className="min-w-0">
        <LinkVoltar para={`${BASE}/painel`} className="inline-flex items-center gap-1 text-sm text-tinta-3 hover:text-tinta-1">
          <ArrowLeft className="size-4" aria-hidden />
          {t('ficha.voltar')}
        </LinkVoltar>
        <TextoCortado como="h2" className="mt-1 text-xl font-semibold tracking-tight">
          {f.nome}
        </TextoCortado>
        {f.descricao && <p className="mt-1 max-w-prose text-sm text-tinta-2">{textoLivre(f.descricao)}</p>}
        <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-tinta-3">
          <Selo cor={COR_ESTADO[f.estado]}>{t(`estados.${f.estado}`)}</Selo>
          <Selo>{t(`areas.${f.area}`)}</Selo>
          <Selo cor={f.modo === 'real' ? 'acento' : 'neutro'}>{t(`modos.${f.modo}`)}</Selo>
          <span>{f.ultimo_reporte_em ? t('ficha.ultimoReporte', { quando: relativo(f.ultimo_reporte_em) }) : t('cartao.nunca')}</span>
        </div>
        {pendentes && <p className="mt-2 text-xs text-tinta-2">{t('ficha.comandosPendentes', { lista: pendentes })}</p>}
      </div>
      {s.pode('automacoes.editar') && (
        <div className="flex flex-wrap items-center gap-2">
          {f.pausada ? (
            <Botao icone={<Play className="size-4" />} carregando={comando.isPending} onClick={() => comando.mutate('retomar')}>
              {t('comandos.retomar')}
            </Botao>
          ) : (
            <>
              <Botao icone={<Pause className="size-4" />} disabled={comando.isPending} onClick={() => comando.mutate('pausar')}>
                {t('comandos.pausar')}
              </Botao>
              <Botao icone={<FlaskConical className="size-4" />} disabled={comando.isPending} onClick={() => comando.mutate('simular')}>
                {t('comandos.simular')}
              </Botao>
              <Botao icone={<Play className="size-4" />} carregando={comando.isPending} onClick={() => comando.mutate('rodar_agora')}>
                {t('comandos.rodar_agora')}
              </Botao>
            </>
          )}
        </div>
      )}
    </div>
  );
}

/** O desenho do trabalho: gatilho, entradas, etapas, decisões e saídas, com o número de cada caixa na última execução. */
function Fluxo({ f }: { f: Ficha }) {
  const { t } = useTranslation('automacoes');
  if (!f.etapas.length) return <Vazio texto={t('vazio.fluxo')} />;
  const temNumeros = Object.keys(f.etapas_ultima).length > 0;
  return (
    <Cartao titulo={temNumeros ? t('ficha.numerosDaUltima') : t('ficha.semNumeros')}>
      <ol className="flex flex-col items-stretch gap-1" data-fluxo>
        {f.etapas.map((e, i) => {
          const n = f.etapas_ultima[e.chave];
          return (
            <li key={e.chave} className="flex flex-col items-center gap-1">
              {i > 0 && <ArrowDown className="size-4 text-tinta-3" aria-hidden />}
              <div className={`flex w-full max-w-xl min-w-0 items-start justify-between gap-3 rounded-lg border bg-sup-2 px-3 py-2 ${e.tipo === 'decisao' ? 'border-dourado' : e.tipo === 'gatilho' ? 'border-acento-forte' : 'border-borda'}`}>
                <div className="min-w-0">
                  <p className="text-xs text-tinta-3">{t(`tiposEtapa.${e.tipo}`)}</p>
                  <p className="text-sm font-medium break-words">{textoLivre(e.titulo)}</p>
                  {e.descricao && <p className="text-xs break-words text-tinta-2">{textoLivre(e.descricao)}</p>}
                </div>
                {n !== undefined && <span className="numero shrink-0 text-lg font-semibold">{numero(n)}</span>}
              </div>
            </li>
          );
        })}
      </ol>
    </Cartao>
  );
}

function Execucoes({ f }: { f: Ficha }) {
  const { t } = useTranslation('automacoes');
  const [filtro, setFiltro] = useState<'todas' | 'falhou' | 'sem_sinal'>('todas');
  const colunas: Coluna<Execucao>[] = [
    { chave: 'iniciada_em', rotulo: t('colunas.inicio'), principal: true, mono: true, render: (x) => dataHora(x.iniciada_em) },
    { chave: 'estado', rotulo: t('colunas.estado'), render: (x) => <Selo cor={COR_EXECUCAO[x.estado]}>{t(`execucao.${x.estado}`)}</Selo> },
    { chave: 'modo', rotulo: t('colunas.modo'), render: (x) => t(`modos.${x.modo}`) },
    { chave: 'duracao_s', rotulo: t('colunas.duracao'), alinhar: 'direita', mono: true, render: (x) => duracao(x.duracao_s) },
    { chave: 'processados', rotulo: t('colunas.processados'), alinhar: 'direita', mono: true },
    { chave: 'ok', rotulo: t('colunas.ok'), alinhar: 'direita', mono: true },
    { chave: 'divergentes', rotulo: t('colunas.divergentes'), alinhar: 'direita', mono: true },
    { chave: 'aguardando', rotulo: t('colunas.aguardando'), alinhar: 'direita', mono: true },
    { chave: 'mensagem', rotulo: t('colunas.mensagem'), render: (x) => textoLivre(x.mensagem) },
    { chave: 'codigo_saida', rotulo: t('colunas.codigoSaida'), mono: true, oculta: true, render: (x) => (x.codigo_saida === null ? '' : String(x.codigo_saida)) },
    { chave: 'id_externo', rotulo: t('colunas.idExterno'), mono: true, oculta: true },
  ];
  return (
    <Tabela<Execucao>
      tela="automacoes.execucoes"
      caminho="/automacoes/execucoes"
      params={{ automacao_id: f.id, ...(filtro === 'todas' ? {} : { estado: filtro }) }}
      chaveConsulta={['automacoes', 'execucoes', f.id, filtro]}
      colunas={colunas}
      filtros={{ filtro }}
      aoAplicarFiltros={(x) => setFiltro(x.filtro === 'falhou' || x.filtro === 'sem_sinal' ? x.filtro : 'todas')}
      filtrosExtra={
        <Segmentado<'todas' | 'falhou' | 'sem_sinal'>
          rotulo={t('filtros.situacao')}
          valor={filtro}
          aoMudar={setFiltro}
          opcoes={[
            { valor: 'todas', rotulo: t('filtros.todas') },
            { valor: 'falhou', rotulo: t('filtros.falhas') },
            { valor: 'sem_sinal', rotulo: t('filtros.semSinal') },
          ]}
        />
      }
      vazio={<Vazio texto={t('vazio.execucoes')} />}
    />
  );
}

function Pendencias({ f }: { f: Ficha }) {
  const { t } = useTranslation('automacoes');
  const [filtro, setFiltro] = useState<'aberta' | 'resolvida' | 'todas'>('aberta');
  const [params, setParams] = useSearchParams();
  const [aberta, setAberta] = useState<Pendencia | null>(null);
  // O link de volta do card do Tarefas (`?pendencia=<id>`) abre a pendência.
  const doLink = params.get('pendencia');
  const daUrl = useQuery({
    queryKey: ['automacoes', 'pendencias', f.id, 'link', doLink],
    queryFn: () => get<{ itens: Pendencia[] }>('/automacoes/pendencias', { automacao_id: f.id, id: doLink! }).then((r) => r.itens[0] ?? null),
    enabled: !!doLink && !aberta,
  });
  const fechar = () => {
    setAberta(null);
    if (doLink)
      setParams(
        (p) => {
          const n = new URLSearchParams(p);
          n.delete('pendencia');
          return n;
        },
        { replace: true },
      );
  };
  const mostrada = aberta ?? (doLink ? daUrl.data : null) ?? null;
  const colunas: Coluna<Pendencia>[] = [
    { chave: 'titulo', rotulo: t('colunas.titulo'), principal: true, render: (x) => <span className="font-medium">{textoLivre(x.titulo)}</span> },
    { chave: 'tipo', rotulo: t('colunas.tipo'), render: (x) => t(`tiposPendencia.${x.tipo}`, { defaultValue: x.tipo }) },
    { chave: 'estado', rotulo: t('colunas.estado'), render: (x) => <Selo cor={x.estado === 'aberta' ? 'laranja' : 'verde'}>{x.estado === 'aberta' ? t('filtros.abertas') : t('filtros.resolvidas')}</Selo> },
    { chave: 'responsavel_nome', rotulo: t('colunas.responsavel'), render: (x) => (x.responsavel_nome ? nomePessoa(x.responsavel_nome) : t('pendencia.semResponsavel')) },
    { chave: 'prazo', rotulo: t('colunas.prazo'), mono: true, render: (x) => (x.prazo ? data(x.prazo) : '') },
    { chave: 'created_at', rotulo: t('colunas.criada'), mono: true, render: (x) => dataHora(x.created_at) },
    { chave: 'card_numero', rotulo: t('colunas.tarefa'), mono: true, render: (x) => (x.card_numero ? t('pendencia.tarefa', { numero: x.card_numero }) : '') },
  ];
  return (
    <>
      <Tabela<Pendencia>
        tela="automacoes.pendencias"
        caminho="/automacoes/pendencias"
        params={{ automacao_id: f.id, ...(filtro === 'todas' ? {} : { estado: filtro }) }}
        chaveConsulta={['automacoes', 'pendencias', f.id, filtro]}
        colunas={colunas}
        filtros={{ filtro }}
        aoAplicarFiltros={(x) => setFiltro(x.filtro === 'resolvida' || x.filtro === 'todas' ? x.filtro : 'aberta')}
        aoClicar={setAberta}
        filtrosExtra={
          <Segmentado<'aberta' | 'resolvida' | 'todas'>
            rotulo={t('filtros.situacao')}
            valor={filtro}
            aoMudar={setFiltro}
            opcoes={[
              { valor: 'aberta', rotulo: t('filtros.abertas') },
              { valor: 'resolvida', rotulo: t('filtros.resolvidas') },
              { valor: 'todas', rotulo: t('filtros.todas') },
            ]}
          />
        }
        vazio={<Vazio texto={t('vazio.pendencias')} />}
      />
      {mostrada && <PainelPendencia p={mostrada} aoFechar={fechar} />}
    </>
  );
}

function PainelPendencia({ p, aoFechar }: { p: Pendencia; aoFechar: () => void }) {
  const { t } = useTranslation('automacoes');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const qc = useQueryClient();
  const toast = useToast();
  const podeEditar = s.pode('automacoes.editar');
  const [virar, setVirar] = useState(false);
  // Entrega 2: "Virar tarefa" com o Tarefas instalado e quem pode criar card; uma tarefa por pendência aberta.
  const podeVirar = podeEditar && p.estado === 'aberta' && !p.card_id && s.instalado('tarefas') && s.pode('tarefas.criar');
  const invalidar = () => qc.invalidateQueries({ queryKey: ['automacoes'] });
  const acao = useMutation({
    mutationFn: (x: { caminho: string; corpo: unknown }) => post(x.caminho, x.corpo),
    onSuccess: async () => {
      toast.sucesso(tn('comum.salvo'));
      await invalidar();
      aoFechar();
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  const campos: DefCampo[] = [
    { nome: 'responsavel_id', rotulo: t('colunas.responsavel'), tipo: 'busca', busca: { caminho: '/membros', rotulo: (i) => nomePessoa(String(i.nome ?? '')) }, desabilitado: !podeEditar },
    { nome: 'prazo', rotulo: t('colunas.prazo'), tipo: 'data', desabilitado: !podeEditar },
  ];
  return (
    <Drawer
      aberto
      aoFechar={aoFechar}
      largura="lg"
      titulo={textoLivre(p.titulo)}
      rodape={
        podeEditar ? (
          <>
            <Botao variante="fantasma" onClick={aoFechar}>
              {tn('comum.cancelar')}
            </Botao>
            <Botao type="submit" form="form-pendencia">
              {t('pendencia.salvarResponsavel')}
            </Botao>
            {podeVirar && (
              <Botao icone={<ListTodo className="size-4" />} onClick={() => setVirar(true)}>
                {t('pendencia.virarTarefa')}
              </Botao>
            )}
            {p.estado === 'aberta' ? (
              <Botao variante="primario" carregando={acao.isPending} onClick={() => acao.mutate({ caminho: `/automacoes/pendencias/${p.id}/resolver`, corpo: {} })}>
                {t('pendencia.resolver')}
              </Botao>
            ) : (
              <Botao variante="primario" carregando={acao.isPending} onClick={() => acao.mutate({ caminho: `/automacoes/pendencias/${p.id}/reabrir`, corpo: {} })}>
                {t('pendencia.reabrir')}
              </Botao>
            )}
          </>
        ) : undefined
      }
    >
      <div className="flex min-w-0 flex-col gap-4">
        <div className="flex flex-wrap items-center gap-2 text-xs text-tinta-3">
          <Selo cor={p.estado === 'aberta' ? 'laranja' : 'verde'}>{p.estado === 'aberta' ? t('filtros.abertas') : t('filtros.resolvidas')}</Selo>
          <span>{t(`tiposPendencia.${p.tipo}`, { defaultValue: p.tipo })}</span>
          <span>{dataHora(p.created_at)}</span>
        </div>
        {p.detalhe && <p className="text-sm break-words text-tinta-2">{textoLivre(p.detalhe)}</p>}
        {p.resolvida_em && <p className="text-sm text-tinta-2">{t('pendencia.resolvida', { quando: dataHora(p.resolvida_em) })}</p>}
        {p.nota && <p className="text-sm break-words text-tinta-2">{textoLivre(p.nota)}</p>}
        {p.link && (
          <a href={p.link} target="_blank" rel="noopener noreferrer" className="inline-flex w-fit items-center gap-1 text-sm text-acento-forte hover:underline">
            <ExternalLink className="size-4" aria-hidden />
            {t('pendencia.abrirLink')}
          </a>
        )}
        {p.card_id &&
          p.card_numero &&
          (s.instalado('tarefas') && s.pode('tarefas.ver') ? (
            <Link to={`/operacao/tarefas/lista?card=${p.card_id}`} className="inline-flex w-fit items-center gap-1 text-sm text-acento-forte hover:underline" data-tarefa-da-pendencia>
              <ListTodo className="size-4" aria-hidden />
              {t('pendencia.tarefa', { numero: p.card_numero })}
            </Link>
          ) : (
            <p className="text-sm text-tinta-2">{t('pendencia.tarefa', { numero: p.card_numero })}</p>
          ))}
        <Formulario
          id="form-pendencia"
          campos={campos}
          inicial={{ responsavel_id: p.responsavel_id ?? '', prazo: p.prazo ?? '' }}
          rotulosAtuais={p.responsavel_nome ? { responsavel_id: nomePessoa(p.responsavel_nome) } : undefined}
          aoEnviar={async (v) => {
            await patch(`/automacoes/pendencias/${p.id}`, { responsavel_id: v.responsavel_id || null, prazo: v.prazo || null });
            await invalidar();
            toast.sucesso(tn('comum.salvo'));
            aoFechar();
          }}
        />
      </div>
      {virar && (
        <VirarTarefa
          p={p}
          aoFechar={() => setVirar(false)}
          aoCriar={async () => {
            setVirar(false);
            await invalidar();
            aoFechar();
          }}
        />
      )}
    </Drawer>
  );
}

/**
 * Entrega 2: a pendência vira um card no Tarefas, no quadro e com o responsável escolhidos (padrão: o quadro padrão e o
 * responsável e o prazo da pendência). O servidor aplica as regras do Tarefas (WIP, membro ativo, aprovação humana).
 */
function VirarTarefa({ p, aoFechar, aoCriar }: { p: Pendencia; aoFechar: () => void; aoCriar: () => Promise<void> }) {
  const { t } = useTranslation('automacoes');
  const { t: tn } = useTranslation();
  const toast = useToast();
  const [quadro, setQuadro] = useState('');
  const [responsavel, setResponsavel] = useState(p.responsavel_id ?? '');
  const [prazo, setPrazo] = useState(p.prazo ?? '');
  const quadros = useQuery({ queryKey: ['automacoes', 'tarefas', 'quadros'], queryFn: () => getTodos<{ id: string; nome: string }>('/tarefas/quadros').then((r) => r.itens) });
  const membros = useQuery({ queryKey: ['automacoes', 'membros'], queryFn: () => getTodos<{ id: string; nome: string }>('/membros').then((r) => r.itens) });
  const criar = useMutation({
    mutationFn: () => post<{ card_id: string; numero: number }>(`/automacoes/pendencias/${p.id}/tarefa`, { quadro_id: quadro || null, responsavel_id: responsavel || null, prazo: prazo || null }),
    onSuccess: async (r) => {
      toast.sucesso(t('pendencia.tarefaCriada', { numero: r.numero }));
      await aoCriar();
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  return (
    <Modal
      aberto
      aoFechar={aoFechar}
      titulo={t('pendencia.virarTitulo')}
      rodape={
        <>
          <Botao variante="fantasma" onClick={aoFechar}>
            {tn('comum.cancelar')}
          </Botao>
          <Botao variante="primario" carregando={criar.isPending} onClick={() => criar.mutate()}>
            {t('pendencia.criarTarefa')}
          </Botao>
        </>
      }
    >
      <div className="flex min-w-0 flex-col gap-4" data-virar-tarefa>
        <p className="text-sm text-tinta-2">{t('pendencia.virarTexto')}</p>
        <Rotulo id="pendencia-quadro" rotulo={t('pendencia.quadro')}>
          <Selecao id="pendencia-quadro" vazio={t('pendencia.quadroPadrao')} value={quadro} onChange={(e) => setQuadro(e.target.value)} opcoes={(quadros.data ?? []).map((x) => ({ valor: x.id, rotulo: x.nome }))} />
        </Rotulo>
        <Rotulo id="pendencia-responsavel" rotulo={t('colunas.responsavel')}>
          <Selecao
            id="pendencia-responsavel"
            vazio={t('pendencia.semResponsavel')}
            value={responsavel}
            onChange={(e) => setResponsavel(e.target.value)}
            opcoes={(membros.data ?? []).map((m) => ({ valor: m.id, rotulo: nomePessoa(m.nome) }))}
          />
        </Rotulo>
        <Rotulo id="pendencia-prazo" rotulo={t('colunas.prazo')}>
          <Entrada id="pendencia-prazo" type="date" value={prazo} onChange={(e) => setPrazo(e.target.value)} />
        </Rotulo>
      </div>
    </Modal>
  );
}

/** Entrega 2 (aba Resultados): os links do que cada execução gerou (relatório, planilha, arquivo, pasta), reportados pelo robô. */
function Resultados({ f }: { f: Ficha }) {
  const { t } = useTranslation('automacoes');
  const [tipo, setTipo] = useState<Resultado['tipo'] | ''>('');
  const colunas: Coluna<Resultado>[] = [
    {
      chave: 'titulo',
      rotulo: t('colunas.resultado'),
      principal: true,
      render: (x) => (
        <a
          href={x.url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex min-w-0 items-center gap-1 font-medium text-acento-forte hover:underline"
          aria-label={t('pendencia.abrirResultado', { titulo: textoLivre(x.titulo) })}
          onClick={(e) => e.stopPropagation()}
        >
          <ExternalLink className="size-4 shrink-0" aria-hidden />
          <span className="min-w-0 truncate" title={textoLivre(x.titulo)}>
            {textoLivre(x.titulo)}
          </span>
        </a>
      ),
    },
    { chave: 'tipo', rotulo: t('colunas.tipo'), render: (x) => t(`tiposResultado.${x.tipo}`, { defaultValue: x.tipo }) },
    { chave: 'execucao_iniciada_em', rotulo: t('colunas.execucao'), mono: true, render: (x) => dataHora(x.execucao_iniciada_em) },
    { chave: 'created_at', rotulo: t('colunas.criada'), mono: true, oculta: true, render: (x) => dataHora(x.created_at) },
  ];
  return (
    <Tabela<Resultado>
      tela="automacoes.resultados"
      caminho="/automacoes/resultados"
      params={{ automacao_id: f.id, ...(tipo ? { tipo } : {}) }}
      chaveConsulta={['automacoes', 'resultados', f.id, tipo]}
      colunas={colunas}
      filtros={{ tipo }}
      aoAplicarFiltros={(x) => setTipo(TIPOS_RESULTADO.includes(x.tipo as Resultado['tipo']) ? (x.tipo as Resultado['tipo']) : '')}
      filtrosExtra={
        <Selecao
          aria-label={t('colunas.tipo')}
          vazio={t('filtros.todosTipos')}
          value={tipo}
          onChange={(e) => setTipo(e.target.value as Resultado['tipo'] | '')}
          opcoes={TIPOS_RESULTADO.map((x) => ({ valor: x, rotulo: t(`tiposResultado.${x}`) }))}
        />
      }
      vazio={<Vazio texto={t('vazio.resultados')} />}
    />
  );
}

const HORARIO = /^([01]\d|2[0-3]):[0-5]\d$/;
const inteiroOuNulo = (v: unknown) => (v === '' || v === null || v === undefined ? null : Number(v));

function Configuracao({ f }: { f: Ficha }) {
  const { t } = useTranslation('automacoes');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const qc = useQueryClient();
  const toast = useToast();
  // Val5 (29/10): a configuração da automação (e o aviso sem sinal e a chave, abaixo) pede `automacoes.configurar`.
  const podeEditar = s.pode('automacoes.configurar');
  const desabilitado = !podeEditar;
  const campos: DefCampo[] = [
    { nome: 'nome', rotulo: t('config.nome'), tipo: 'texto', obrigatorio: true, desabilitado },
    { nome: 'area', rotulo: t('config.area'), tipo: 'selecao', obrigatorio: true, largura: 'meia', opcoes: AREAS.map((a) => ({ valor: a, rotulo: t(`areas.${a}`) })), desabilitado },
    { nome: 'modo', rotulo: t('config.modo'), tipo: 'selecao', obrigatorio: true, largura: 'meia', ajuda: t('config.modoAjuda'), opcoes: (['simulacao', 'real'] as const).map((m) => ({ valor: m, rotulo: t(`modos.${m}`) })), desabilitado },
    { nome: 'descricao', rotulo: t('config.descricao'), tipo: 'area', desabilitado },
    { nome: 'frequencia', rotulo: t('config.frequencia'), tipo: 'selecao', obrigatorio: true, largura: 'meia', opcoes: (['dias_uteis', 'mensal', 'manual'] as const).map((x) => ({ valor: x, rotulo: t(`frequencias.${x}`) })), desabilitado },
    {
      nome: 'horarios',
      rotulo: t('config.horarios'),
      tipo: 'texto',
      largura: 'meia',
      ajuda: t('config.horariosAjuda'),
      visivel: (v) => v.frequencia !== 'manual',
      validar: (v) => (String(v ?? '').split(',').map((x) => x.trim()).filter(Boolean).every((x) => HORARIO.test(x)) ? null : t('config.horarioInvalido')),
      desabilitado,
    },
    { nome: 'dia_inicio', rotulo: t('config.diaInicio'), tipo: 'numero', min: 1, max: 31, largura: 'terco', visivel: (v) => v.frequencia === 'dias_uteis', desabilitado },
    { nome: 'dia_fim', rotulo: t('config.diaFim'), tipo: 'numero', min: 1, max: 31, largura: 'terco', visivel: (v) => v.frequencia === 'dias_uteis', desabilitado },
    { nome: 'dia_util_inicio', rotulo: t('config.diaUtilInicio'), tipo: 'numero', min: 1, max: 23, largura: 'terco', visivel: (v) => v.frequencia === 'dias_uteis', desabilitado },
    { nome: 'dia_mes', rotulo: t('config.diaMes'), tipo: 'numero', min: 1, max: 31, largura: 'meia', visivel: (v) => v.frequencia === 'mensal', desabilitado },
  ];
  const inicial = {
    nome: f.nome,
    area: f.area,
    modo: f.modo,
    descricao: f.descricao ?? '',
    frequencia: f.agenda.frequencia,
    horarios: f.agenda.horarios.join(', '),
    dia_inicio: f.agenda.dia_inicio ?? '',
    dia_fim: f.agenda.dia_fim ?? '',
    dia_util_inicio: f.agenda.dia_util_inicio ?? '',
    dia_mes: f.agenda.dia_mes ?? '',
  };
  return (
    <div className="flex min-w-0 flex-col gap-6">
      <Cartao titulo={t('config.geral')}>
        <p className="mb-3 text-xs text-tinta-3">{t('config.codigo', { codigo: f.codigo })}</p>
        <Formulario
          key={`${f.id}-${f.nome}-${f.modo}-${JSON.stringify(f.agenda)}`}
          id="form-config-automacao"
          campos={campos}
          inicial={inicial}
          aoEnviar={async (v) => {
            const frequencia = String(v.frequencia);
            await patch(`/automacoes/automacoes/${f.id}`, {
              nome: v.nome,
              area: v.area,
              modo: v.modo,
              descricao: v.descricao || null,
              agenda: {
                frequencia,
                horarios: frequencia === 'manual' ? [] : String(v.horarios ?? '').split(',').map((x) => x.trim()).filter(Boolean),
                dia_inicio: inteiroOuNulo(v.dia_inicio),
                dia_fim: inteiroOuNulo(v.dia_fim),
                dia_util_inicio: inteiroOuNulo(v.dia_util_inicio),
                dia_mes: inteiroOuNulo(v.dia_mes),
              },
            });
            await qc.invalidateQueries({ queryKey: ['automacoes'] });
            toast.sucesso(tn('comum.salvo'));
          }}
        />
        {podeEditar && (
          <div className="mt-4 flex justify-end">
            <Botao variante="primario" type="submit" form="form-config-automacao">
              {tn('comum.salvar')}
            </Botao>
          </div>
        )}
      </Cartao>
      <Avisos f={f} />
      <ChaveDoRobo f={f} />
    </div>
  );
}

/**
 * Entrega 2: quem recebe os avisos de falha, pendência e "sem sinal" (vazio = quem tem acesso às automações da área) e as
 * horas sem reporte até o "sem sinal" (vazio = o padrão pela agenda).
 */
function Avisos({ f }: { f: Ficha }) {
  const { t } = useTranslation('automacoes');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const qc = useQueryClient();
  const toast = useToast();
  const podeEditar = s.pode('automacoes.configurar');
  const [lista, setLista] = useState(f.aviso_usuarios);
  const [horas, setHoras] = useState(f.horas_sem_sinal === null ? '' : String(f.horas_sem_sinal));
  const membros = useQuery({ queryKey: ['automacoes', 'membros'], queryFn: () => getTodos<{ id: string; nome: string }>('/membros').then((r) => r.itens), enabled: podeEditar });
  const horasValidas = horas === '' || (/^\d+$/.test(horas) && Number(horas) >= 1 && Number(horas) <= 168);
  const salvar = useMutation({
    mutationFn: () => patch(`/automacoes/automacoes/${f.id}`, { aviso_usuarios: lista.map((u) => u.id), horas_sem_sinal: horas === '' ? null : Number(horas) }),
    onSuccess: async () => {
      toast.sucesso(tn('comum.salvo'));
      await qc.invalidateQueries({ queryKey: ['automacoes'] });
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  return (
    <Cartao titulo={t('config.avisos')}>
      <div className="flex min-w-0 flex-col gap-4" data-avisos-automacao>
        <p className="text-sm text-tinta-2">{t('config.avisosTexto')}</p>
        <div className="flex min-w-0 flex-col gap-2">
          {!lista.length && <p className="text-sm text-tinta-3">{t('config.avisoTodos')}</p>}
          {!!lista.length && (
            <ul className="flex flex-wrap gap-2">
              {lista.map((u) => (
                <li key={u.id} className="flex items-center gap-1">
                  <Selo>{nomePessoa(u.nome)}</Selo>
                  {podeEditar && (
                    <Botao
                      variante="fantasma"
                      aria-label={t('config.avisoRemover', { nome: nomePessoa(u.nome) })}
                      title={t('config.avisoRemover', { nome: nomePessoa(u.nome) })}
                      icone={<X className="size-4" />}
                      onClick={() => setLista(lista.filter((x) => x.id !== u.id))}
                    />
                  )}
                </li>
              ))}
            </ul>
          )}
          {podeEditar && membros.data && (
            <Rotulo id="automacao-aviso-incluir" rotulo={t('config.avisoIncluir')} ajuda={t('config.avisoAjuda')}>
              <Selecao
                id="automacao-aviso-incluir"
                vazio={t('config.avisoIncluir')}
                value=""
                onChange={(e) => {
                  const m = membros.data?.find((x) => x.id === e.target.value);
                  if (m) setLista([...lista, m]);
                }}
                opcoes={membros.data.filter((m) => !lista.some((x) => x.id === m.id)).map((m) => ({ valor: m.id, rotulo: nomePessoa(m.nome) }))}
              />
            </Rotulo>
          )}
        </div>
        <Rotulo
          id="automacao-horas-sem-sinal"
          rotulo={t('config.horasSemSinal')}
          ajuda={t('config.horasSemSinalAjuda', { padrao: f.horas_sem_sinal_padrao })}
          erro={horasValidas ? undefined : t('config.horasFaixa')}
        >
          <Entrada
            id="automacao-horas-sem-sinal"
            type="number"
            inputMode="numeric"
            min={1}
            max={168}
            disabled={!podeEditar}
            value={horas}
            placeholder={String(f.horas_sem_sinal_padrao)}
            aria-invalid={horasValidas ? undefined : true}
            aria-describedby={horasValidas ? 'automacao-horas-sem-sinal-ajuda' : 'automacao-horas-sem-sinal-erro'}
            onChange={(e) => setHoras(e.target.value)}
            className="numero"
          />
        </Rotulo>
        {podeEditar && (
          <div className="flex justify-end">
            <Botao variante="primario" disabled={!horasValidas} carregando={salvar.isPending} onClick={() => salvar.mutate()}>
              {tn('comum.salvar')}
            </Botao>
          </div>
        )}
      </div>
    </Cartao>
  );
}

/** Chave do robô: mostrada uma vez ao gerar; depois, só o prefixo, a data e o último uso. */
function ChaveDoRobo({ f }: { f: Ficha }) {
  const { t } = useTranslation('automacoes');
  const s = useSessao();
  const qc = useQueryClient();
  const toast = useToast();
  const [confirmarGerar, setConfirmarGerar] = useState(false);
  const [confirmarRevogar, setConfirmarRevogar] = useState(false);
  const [nova, setNova] = useState<string | null>(null);
  const invalidar = () => Promise.all([qc.invalidateQueries({ queryKey: ['automacoes', 'ficha', f.id] }), qc.invalidateQueries({ queryKey: chavePainel })]);
  const gerar = useMutation({
    mutationFn: () => post<{ chave: string }>(`/automacoes/automacoes/${f.id}/chave`, {}),
    onSuccess: async (r) => {
      setConfirmarGerar(false);
      setNova(r.chave);
      await invalidar();
    },
    onError: (e) => {
      setConfirmarGerar(false);
      toast.erro(mensagemDeErro(e));
    },
  });
  const revogar = useMutation({
    mutationFn: () => del(`/automacoes/automacoes/${f.id}/chave`),
    onSuccess: async () => {
      setConfirmarRevogar(false);
      toast.sucesso(t('config.revogada'));
      await invalidar();
    },
    onError: (e) => {
      setConfirmarRevogar(false);
      toast.erro(mensagemDeErro(e));
    },
  });
  const podeEditar = s.pode('automacoes.configurar');
  const variavel = f.codigo.toUpperCase().replace(/-/g, '_');
  return (
    <Cartao
      titulo={t('config.chave')}
      acao={
        podeEditar ? (
          <div className="flex flex-wrap gap-2">
            {f.chave && (
              <Botao variante="perigo" tamanho="sm" onClick={() => setConfirmarRevogar(true)}>
                {t('config.revogar')}
              </Botao>
            )}
            <Botao tamanho="sm" icone={<KeyRound className="size-4" />} carregando={gerar.isPending} onClick={() => (f.chave ? setConfirmarGerar(true) : gerar.mutate())}>
              {f.chave ? t('config.gerarDeNovo') : t('config.gerar')}
            </Botao>
          </div>
        ) : undefined
      }
    >
      <p className="text-sm text-tinta-2">{t('config.chaveTexto')}</p>
      <p className="mt-2 text-sm" data-chave-robo>
        {f.chave ? (
          <>
            {t('config.chaveAtiva', { prefixo: `mga_${f.chave.prefixo}`, criada: dataHora(f.chave.criada_em) })}
            {', '}
            {f.chave.ultimo_uso_em ? t('config.ultimoUso', { quando: relativo(f.chave.ultimo_uso_em) }) : t('config.nuncaUsada')}
          </>
        ) : (
          <span className="text-tinta-3">{t('config.semChave')}</span>
        )}
      </p>
      <Confirmar aberto={confirmarGerar} aoFechar={() => setConfirmarGerar(false)} aoConfirmar={() => gerar.mutate()} titulo={t('config.gerarTitulo')} texto={t('config.gerarTexto')} carregando={gerar.isPending} />
      <Confirmar aberto={confirmarRevogar} aoFechar={() => setConfirmarRevogar(false)} aoConfirmar={() => revogar.mutate()} titulo={t('config.revogarTitulo')} texto={t('config.revogarTexto')} perigoso carregando={revogar.isPending} />
      <Modal aberto={!!nova} aoFechar={() => setNova(null)} titulo={t('config.novaTitulo')}>
        <div className="flex min-w-0 flex-col gap-3">
          <p className="text-sm text-tinta-2">{t('config.novaTexto')}</p>
          <code className="numero block min-w-0 rounded-lg border border-borda bg-sup-2 p-3 text-xs break-all" data-mascarado>
            {nova}
          </code>
          <p className="text-xs text-tinta-3">{t('config.variavel', { codigo: variavel })}</p>
          {nova && (
            <Botao
              tamanho="sm"
              className="w-fit"
              icone={<Copy className="size-4" />}
              onClick={() =>
                void navigator.clipboard
                  ?.writeText(nova)
                  .then(() => toast.sucesso(t('config.copiada')))
                  .catch(() => toast.erro(t('config.copiar')))
              }
            >
              {t('config.copiar')}
            </Botao>
          )}
        </div>
      </Modal>
    </Cartao>
  );
}
